import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';

import { TextButton } from '@components/Button/variants';
import IconClose from '@components/Images/Rebranding/IconClose';
import YouTubeIframe, { extractVideoId } from '@components/YouTube/YouTubeIframe';
import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, TrackingLocation, click } from '@tracking';

import { AppUsedIcon } from './types';

interface BlueprintVideoPlayerProps {
  videoUrl: string;
  title: string;
  appsUsed?: AppUsedIcon[];
  onClose: () => void;
}

const BlueprintVideoPlayer = ({
  videoUrl,
  title,
  appsUsed = [],
  onClose,
}: BlueprintVideoPlayerProps) => {
  const { trackEvent } = useEventTracking({
    location: TrackingLocation.Videos,
  });
  const videoId = useMemo(() => extractVideoId(videoUrl), [videoUrl]);

  useEffect(() => {
    if (!videoId) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [videoId, onClose]);

  if (!videoId) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/80"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <figure
        className="relative m-0 w-full max-w-[960px] px-16 sm:px-24"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-grayscale-beige px-20 pb-20">
          <div className="flex items-center justify-between gap-16 py-16">
            <div className="flex min-w-0 items-center gap-12">
              <span className="truncate font-suisse-neue text-subheadline-sm text-grayscale-charcoal">
                {title}
              </span>
              {appsUsed.length > 0 && (
                <div className="flex shrink-0 gap-8">
                  {appsUsed.map((app) => (
                    <div
                      key={app.name}
                      className="flex size-[32px] items-center justify-center rounded-8 bg-white"
                    >
                      <img
                        src={app.iconUrl}
                        alt={app.name}
                        className="size-[20px] object-contain"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
            <TextButton
              as="button"
              onClick={onClose}
              onClickCapture={() =>
                trackEvent(click({ clickTarget: ClickTarget.Close, clickText: title }))
              }
              className="p-0 text-grayscale-charcoal hover:!text-grayscale-charcoal"
              aria-label="Close"
            >
              <IconClose className="block w-16 cursor-pointer stroke-2" />
            </TextButton>
          </div>
          <YouTubeIframe
            videoId={videoId}
            aspectClassName="aspect-[16/9]"
            className="bg-black *:h-full *:w-full"
            playerVars={{ autoplay: 1 }}
          />
        </div>
      </figure>
    </div>,
    document.body
  );
};

export default BlueprintVideoPlayer;
